import React, { useState } from 'react';
import { Container, Card, Button, Row, Col, Form, Alert, Table } from 'react-bootstrap';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { Select, DatePicker } from 'antd';
import FormItem from '../common/FormItem/FormItem';
import DateRangePicker from '../common/DateRangePicker/DateRangePicker';
import PageTitle from '../common/PageTitle/PageTitle';
import styles from './product-inventory.module.scss';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// Mock data for ALL product inventory across all products
const allProductInventory = [
  // KrossKast Player 8 devices
  {
    id: 'device-8-1',
    productName: 'KrossKast Player 8',
    serialNumber: 'KKP8-001-ABC123',
    customId: 'Not assigned',
    status: 'AVAILABLE',
    location: 'Warehouse',
    lastSeen: '2024-01-15 14:30:00',
    assignedDate: null,
    brand: 'KrossKast',
    contactEmail: null,
    contactPhone: null
  },
  {
    id: 'device-8-2',
    productName: 'KrossKast Player 8',
    serialNumber: 'KKP8-002-DEF456',
    customId: 'Not assigned',
    status: 'AVAILABLE',
    location: 'Warehouse',
    lastSeen: '2024-01-14 09:15:00',
    assignedDate: null,
    brand: 'KrossKast',
    contactEmail: null,
    contactPhone: null
  },
  {
    id: 'device-8-3',
    productName: 'KrossKast Player 8',
    serialNumber: 'KKP8-003-GH1789',
    customId: 'Not assigned',
    status: 'AVAILABLE',
    location: 'Warehouse',
    lastSeen: '2024-01-13 16:45:00',
    assignedDate: null,
    brand: 'KrossKast',
    contactEmail: null,
    contactPhone: null
  },
  // KrossKast Player 11 devices
  {
    id: 'device-11-1',
    productName: 'KrossKast Player 11',
    serialNumber: 'KKP11-001-XYZ789',
    customId: 'Not assigned',
    status: 'AVAILABLE',
    location: 'Warehouse',
    lastSeen: '2024-01-12 11:20:00',
    assignedDate: null,
    brand: 'KrossKast',
    contactEmail: null,
    contactPhone: null
  },
  {
    id: 'device-11-2',
    productName: 'KrossKast Player 11',
    serialNumber: 'KKP11-002-UVW456',
    customId: 'Not assigned',
    status: 'AVAILABLE',
    location: 'Warehouse',
    lastSeen: '2024-01-11 08:30:00',
    assignedDate: null,
    brand: 'KrossKast',
    contactEmail: null,
    contactPhone: null
  },
  {
    id: 'device-11-3',
    productName: 'KrossKast Player 11',
    serialNumber: 'KKP11-003-RST123',
    customId: 'Not assigned',
    status: 'AVAILABLE',
    location: 'Warehouse',
    lastSeen: '2024-01-10 15:10:00',
    assignedDate: null,
    brand: 'KrossKast',
    contactEmail: null,
    contactPhone: null
  }
];

const ProductInventory: React.FC = () => {
  const [filters, setFilters] = useState<{
    serialNumber: string | null;
    status: string | null;
    location: string | null;
    productName: string | null;
    playTimeStart: string | null;
    playTimeEnd: string | null;
  }>({
    serialNumber: null,
    status: null,
    location: null,
    productName: null,
    playTimeStart: null,
    playTimeEnd: null
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Filter options - generated from actual data
  const filterOptions = {
    products: [...new Set(allProductInventory.map(device => device.productName))].map(product => ({
      value: product.toLowerCase().replace(/\s+/g, '-'),
      label: product
    })),
    statuses: [...new Set(allProductInventory.map(device => device.status))].map(status => ({
      value: status.toLowerCase(),
      label: status
    })),
    locations: [...new Set(allProductInventory.map(device => device.location))].map(location => ({
      value: location.toLowerCase().replace(/\s+/g, '-'),
      label: location
    }))
  };

  const selectInputs = [
    {
      label: "Serial Number",
      name: "serialNumber",
      placeholder: "Search by serial number",
      options: allProductInventory.map(device => ({ value: device.serialNumber, label: device.serialNumber }))
    },
    { 
      label: "Status", 
      name: "status", 
      placeholder: "Any status",
      options: filterOptions.statuses
    },
    { 
      label: "Location", 
      name: "location", 
      placeholder: "Any location",
      options: filterOptions.locations
    },
  ];

  // Filtering logic
  const filteredDevices = allProductInventory.filter(device => {
    const matchesSerialNumber = !filters.serialNumber || 
      device.serialNumber.toLowerCase().includes(filters.serialNumber.toLowerCase());
    const matchesStatus = !filters.status || 
      device.status.toLowerCase() === filters.status;
    const matchesLocation = !filters.location || 
      device.location.toLowerCase().replace(/\s+/g, '-') === filters.location;
    const matchesProduct = !filters.productName || 
      device.productName.toLowerCase().replace(/\s+/g, '-') === filters.productName;
    
    return matchesSerialNumber && matchesStatus && matchesLocation && matchesProduct;
  });

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateChange = (dates: any) => {
    if (dates) {
      const [playTimeStart, playTimeEnd] = dates;
      setFilters(prev => ({
        ...prev,
        playTimeStart: playTimeStart.utc().format("YYYY-MM-DD"),
        playTimeEnd: playTimeEnd.utc().format("YYYY-MM-DD"),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        playTimeStart: null,
        playTimeEnd: null,
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      serialNumber: null,
      status: null,
      location: null,
      productName: null,
      playTimeStart: null,
      playTimeEnd: null
    });
    setResetCounter(prev => prev + 1);
  };

  return (
    <Container fluid>
      <Form>
        <Card className={styles.card}>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title="Product Inventory" />
              <Button 
                onClick={handleResetFilters}
                style={{ 
                  background: 'white', 
                  color: '#333', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500'
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </Card.Title>
          
            {/* Filters */}
            <Row className="my-2">
              <Col>
                <Row>
                  {selectInputs.map((input) => (
                    <Col key={input.name} md={4}>
                      <FormItem
                        name={input.name}
                        label={input.label}
                        className="customSelectWrapper"
                      >
                        <Select
                          key={`${input.name}-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className="customSelect"
                          placeholder={input.placeholder}
                          value={filters[input.name as keyof typeof filters]}
                          onChange={(value) => setFilters(prev => ({ ...prev, [input.name]: value }))}
                          options={input.options}
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                        />
                      </FormItem>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
            <Row className="my-2">
              <Col
                sm={12}
                lg={6}
                className="d-flex flex-column justify-content-end"
              >
                <FormItem
                  name="dateRange"
                  validateTrigger="onFinish"
                  label="Filter by Date Range"
                  className="mb-0"
                >
                  <DateRangePicker
                    name="dateRange"
                    maxDate={dayjs()}
                    onChange={handleDateChange}
                  />
                </FormItem>
              </Col>
            </Row>
            
            {/* Device Management Section */}
            <div className={styles.deviceSection}>
              <div className={styles.deviceHeader}>
                <h4 className={styles.deviceTitle}>All Product Inventory</h4>
                <Button 
                  variant="primary" 
                  size="sm"
                  className={styles.addDeviceButton}
                >
                  <PlusOutlined /> Add Device
                </Button>
              </div>

              <Table hover responsive className={`legacy-row-height-table ${styles.inventoryTable}`}>
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Serial Number</th>
                    <th>Custom ID</th>
                    <th>Status</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        <div className="text-muted">No devices found matching your filters.</div>
                      </td>
                    </tr>
                  ) : (
                    filteredDevices.map((device) => (
                      <tr key={device.id}>
                        <td>{device.productName}</td>
                        <td>{device.serialNumber}</td>
                        <td>{device.customId}</td>
                        <td>
                          <span className={`badge ${styles.statusBadge} ${styles.statusAvailable}`}>
                            {device.status}
                          </span>
                        </td>
                        <td>{device.location}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className={styles.actionButton}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className={styles.actionButton}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </Container>
  );
};

export default ProductInventory;
